import axios from 'axios';
import qs from 'qs';

import { transformKeysToCamelCase, transformKeysToSnakeCase } from '@/utils/convertKeys';

const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL;
const CAS_BASE_URL = import.meta.env.VITE_CAS_BASE_URL;

const pendingRequests: Record<string, AbortController> = {};

axios.defaults.baseURL = BACKEND_BASE_URL || '';

const httpClient = axios.create({
  withCredentials: true,
  paramsSerializer: (params) => qs.stringify(transformKeysToSnakeCase(params), { arrayFormat: 'brackets' }),
  transformRequest: [(data) => (data ? transformKeysToSnakeCase(data) : undefined), ...(axios.defaults.transformRequest as [])],
  transformResponse: [...(axios.defaults.transformResponse as []), transformKeysToCamelCase]
});

async function initCSRF() {
  try {
    const { data } = await axios.get('/api/csrf', { withCredentials: true });
    const token = data.data.csrf_token;
    httpClient.defaults.headers.common['X-CSRF-Token'] = token;
  } catch (error) {
    console.error('Failed to initialize CSRF token:', error);
  }
}

const removePendingRequest = (url?: string, abort = false): void => {
  if (url && pendingRequests[url]) {
    if (abort) pendingRequests[url].abort();

    delete pendingRequests[url];
  }
};

httpClient.interceptors.request.use((config) => {
  if (config?.url && !config.signal && !config.noAbort) {
    removePendingRequest(config.url, true);

    const abortController = new AbortController();
    config.signal = abortController.signal;
    pendingRequests[config.url] = abortController;
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    removePendingRequest(response.request.responseURL);
    return response;
  },
  async (error) => {
    const url = new URL(window.location.href);
    const currentPath = url.pathname + url.search;

    if (error.response?.status === 401) {
      return window.location.href = `${CAS_BASE_URL}login?service=${encodeURI(`${BACKEND_BASE_URL}sessions/new?path=${encodeURI(currentPath)}`)}`;
    }

    if (error.response?.status === 422 && error.response.data?.errorCode === 'invalid_csrf_token') {
      const originalRequest = error.config as typeof error.config & { _csrfRetried?: boolean };

      if (originalRequest && !originalRequest._csrfRetried) {
        originalRequest._csrfRetried = true;

        await initCSRF();

        if (originalRequest.url) removePendingRequest(originalRequest.url);

        const csrfToken = httpClient.defaults.headers.common['X-CSRF-Token'];
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['X-CSRF-Token'] = csrfToken;

        delete originalRequest.signal;

        return httpClient.request(originalRequest);
      }
    }

    removePendingRequest(error.config?.url);
    return Promise.reject(error);
  }
);

export { initCSRF };
export default httpClient;
